// Daily Focus — Dashboard strip (rotating-subject daily set).
// Single-row action strip ("🎯 Today's Biology · 2/10    Start ›").
// Same one-line shape as AptitudeDrillCard so the above-the-fold doesn't
// inflate; the difference is the subject is DYNAMIC (chosen per student/day
// by the backend rotation) instead of hardcoded "Aptitude".
//
// Polls the READ-ONLY /student/daily-focus/status on mount + every 5 min + on
// window focus — NOT /today, which is find-or-create. A passive dashboard load
// must not stamp a session (that inflated adoption + littered empty rows); the
// session is created only when the student taps in (→ /daily-focus/today).
// Self-hides if the state can't load (e.g. no content for the chosen subject).

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dailyFocusAPI, FocusStatusResponse } from '../services/dailyFocusAPI';
import { track } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

const POLL_MS = 5 * 60 * 1000;

const DailyFocusCard: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const [focus, setFocus] = useState<FocusStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await dailyFocusAPI.status();
        if (!cancelled) setFocus(data);
      } catch {
        // Silent — the strip just stays hidden if we can't load it.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const id = window.setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  if (loading || !focus || !focus.subject) return null;

  const answered = focus.answered;
  const total = focus.total;
  const completed = focus.status === 'completed' || answered >= total;

  const variant: 'fresh' | 'partial' | 'done' = completed
    ? 'done'
    : answered > 0
      ? 'partial'
      : 'fresh';

  const emoji = completed ? '✓' : answered > 0 ? '•' : '🎯';
  const actionLabel = completed ? 'Done' : answered > 0 ? 'Continue' : 'Start';
  const counter = `${answered}/${total}`;

  const palettes = {
    fresh: { bg: '#eff6ff', border: '#93c5fd', emoji: '#1d4ed8', action: '#1d4ed8' },
    partial: { bg: '#fef9c3', border: '#fde047', emoji: '#a16207', action: '#a16207' },
    done: { bg: '#f1f5f9', border: '#cbd5e1', emoji: '#16a34a', action: '#16a34a' },
  } as const;
  // Dark-mode palettes: tinted fills / brighter fg so the strip reads on a
  // dark page instead of glaring white.
  const darkPalettes = {
    fresh: {
      bg: 'rgba(93, 173, 226, 0.12)',
      border: 'rgba(93, 173, 226, 0.35)',
      emoji: '#7cb3ff',
      action: '#7cb3ff',
    },
    partial: {
      bg: 'rgba(245, 176, 65, 0.12)',
      border: 'rgba(245, 176, 65, 0.35)',
      emoji: '#f5c563',
      action: '#f5c563',
    },
    done: {
      bg: 'var(--bg-inset)',
      border: 'var(--border-default)',
      emoji: '#6bd08a',
      action: '#6bd08a',
    },
  } as const;
  const p = (dark ? darkPalettes : palettes)[variant];
  const textPrimary = dark ? '#e6e8eb' : '#0f172a';
  const textMuted = dark ? '#aab1ba' : '#64748b';

  return (
    <button
      data-testid="daily-focus-card"
      onClick={() => {
        track('daily_focus_card.clicked', {
          variant,
          subject: focus.subject,
          answered,
          total,
        });
        navigate('/daily-focus/today?from=card');
      }}
      type="button"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '10px 14px',
        background: p.bg,
        border: `1.5px solid ${p.border}`,
        borderRadius: 12,
        cursor: completed ? 'default' : 'pointer',
        marginBottom: 10,
        fontFamily: 'inherit',
      }}
      aria-label={`${actionLabel} today's ${focus.subject} set — ${counter}`}
    >
      <span
        aria-hidden="true"
        style={{ width: 22, textAlign: 'center', fontSize: 16, color: p.emoji, fontWeight: 700 }}
      >
        {emoji}
      </span>
      <span
        style={{
          flex: 1,
          textAlign: 'left',
          color: textPrimary,
          fontWeight: 600,
          fontSize: 14,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        Today's{' '}
        <span data-testid="daily-focus-subject" style={{ fontWeight: 700 }}>
          {focus.subject}
        </span>{' '}
        <span style={{ color: textMuted, fontWeight: 500 }}>· {counter}</span>
      </span>
      <span style={{ color: p.action, fontSize: 13, fontWeight: 700, letterSpacing: 0.1 }}>
        {actionLabel}
      </span>
      <span style={{ color: textMuted, fontSize: 18, lineHeight: 1, marginLeft: 2 }}>›</span>
    </button>
  );
};

export default DailyFocusCard;
